import React from "react";
import { Card, CardContent } from "../components/ui/card";
import { Stamp, Zap, CheckCircle, Star } from "lucide-react";

const Stamps = () => {
  const stampFeatures = [
    {
      icon: <Star className="h-8 w-8 text-amber-600" />,
      title: "Ottone di Qualità",
      description: "Utilizziamo solo ottone di prima scelta per garantire durata e precisione"
    },
    {
      icon: <Zap className="h-8 w-8 text-amber-600" />,
      title: "Produzione Rapida",
      description: "Tempi di lavorazione ridotti per soddisfare le vostre esigenze"
    },
    {
      icon: <CheckCircle className="h-8 w-8 text-amber-600" />,
      title: "Su Misura",
      description: "Realizziamo timbri secondo le vostre specifiche esatte"
    }
  ];

  const applications = [
    "Marcatura pelletteria",
    "Identificazione prodotti",
    "Timbri personalizzati",
    "Marchi aziendali",
    "Codici e numeri",
    "Loghi personalizzati"
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-r from-amber-600 to-amber-700 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <Stamp className="h-16 w-16 mx-auto mb-6 text-amber-200" />
            <h1 className="text-5xl font-bold mb-6">TIMBRI</h1>
            <p className="text-xl text-amber-100 leading-relaxed">
              Si realizzano timbri in ottone e fustelle in acciaio ricavate dal pieno 
              come da vostro specifico disegno
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-16 items-center mb-20">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Timbri di Precisione
              </h2>
              <div className="space-y-6 text-lg text-gray-700 leading-relaxed">
                <p>
                  Si realizzano timbri in ottone e fustelle in acciaio ricavate dal pieno 
                  come da vostro specifico disegno. La precisione e la qualità dei materiali 
                  utilizzati garantiscono un risultato duraturo e professionale.
                </p>
                <p>
                  Ogni timbro è realizzato artigianalmente seguendo le vostre specifiche, 
                  garantendo precisione millimetrica e dettagli perfetti per ogni applicazione.
                </p>
              </div>
            </div>
            <div>
              <video
                autoPlay
                muted
                loop
                playsInline
                className="w-full h-96 object-cover rounded-lg shadow-xl"
              >
                <source src="https://customer-assets.emergentagent.com/job_modern-ideal/artifacts/56gam2av_realizazzione%20timbro.MOV" type="video/quicktime" />
                <source src="https://customer-assets.emergentagent.com/job_modern-ideal/artifacts/56gam2av_realizazzione%20timbro.MOV" type="video/mp4" />
                {/* Fallback image in case video doesn't load */}
                <img
                  src="https://customer-assets.emergentagent.com/job_b6387205-1c39-4170-9826-13890af1b37d/artifacts/jdtyl4yv_timbro%201.png"
                  alt="Timbri in ottone"
                  className="w-full h-96 object-cover rounded-lg shadow-xl"
                />
              </video>
            </div>
          </div>

          {/* Features */}
          <div className="grid md:grid-cols-3 gap-8 mb-20">
            {stampFeatures.map((feature, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-shadow duration-300">
                <CardContent className="p-8 text-center">
                  <div className="mb-6 flex justify-center">
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {feature.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Gallery */}
          <div className="mb-20">
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
              I Nostri Timbri
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              <Card className="overflow-hidden hover:shadow-lg transition-shadow duration-300">
                <CardContent className="p-0">
                  <img
                    src="https://customer-assets.emergentagent.com/job_b6387205-1c39-4170-9826-13890af1b37d/artifacts/jdtyl4yv_timbro%201.png"
                    alt="Timbro in ottone 1"
                    className="w-full h-64 object-cover"
                  />
                  <div className="p-6">
                    <h3 className="font-semibold text-gray-900 mb-2">Timbri in Ottone</h3>
                    <p className="text-gray-600">Precisione e durata garantite</p>
                  </div>
                </CardContent>
              </Card>

              <Card className="overflow-hidden hover:shadow-lg transition-shadow duration-300">
                <CardContent className="p-0">
                  <img
                    src="https://customer-assets.emergentagent.com/job_b6387205-1c39-4170-9826-13890af1b37d/artifacts/bl9qvyf2_timbro%202.png"
                    alt="Timbro in ottone 2"
                    className="w-full h-64 object-cover"
                  />
                  <div className="p-6">
                    <h3 className="font-semibold text-gray-900 mb-2">Timbri Personalizzati</h3>
                    <p className="text-gray-600">Su vostro specifico disegno</p>
                  </div>
                </CardContent>
              </Card>

              <Card className="overflow-hidden hover:shadow-lg transition-shadow duration-300">
                <CardContent className="p-0">
                  <img
                    src="https://customer-assets.emergentagent.com/job_modern-ideal/artifacts/yw45zdx5_IMG_2186.jpeg"
                    alt="Timbro in ottone di precisione"
                    className="w-full h-64 object-cover"
                  />
                  <div className="p-6">
                    <h3 className="font-semibold text-gray-900 mb-2">Lavorazione di Precisione</h3>
                    <p className="text-gray-600">Dettagli millimetrici</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Applications */}
          <div className="bg-gray-50 rounded-2xl p-12 mb-20">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Applicazioni
              </h2>
              <p className="text-lg text-gray-600">
                I nostri timbri sono utilizzati in diversi settori
              </p>
            </div>
            <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-6">
              {applications.map((application, index) => (
                <div key={index} className="text-center">
                  <div className="bg-white rounded-lg p-6 shadow-md hover:shadow-lg transition-shadow duration-300">
                    <Stamp className="h-8 w-8 text-amber-600 mx-auto mb-3" />
                    <p className="font-medium text-gray-800">{application}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Processo Piastre per Stampa su Pelle */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Piastre per Stampa su Pelle
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Dall'idea al prodotto finito: il processo completo di realizzazione delle piastre per stampa su pelle
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* 1. Video della realizzazione */}
            <div className="text-center">
              <div className="bg-amber-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl font-bold text-amber-600">1</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Lavorazione della Piastra</h3>
              <div className="bg-gray-50 rounded-xl p-4 mb-4">
                <video
                  autoPlay
                  muted
                  loop
                  playsInline
                  className="w-full h-64 object-cover rounded-lg shadow-lg"
                >
                  <source src="https://customer-assets.emergentagent.com/job_modern-ideal/artifacts/64q1ezfx_video%20piastra%201.MOV" type="video/mp4" />
                  Impossibile riprodurre il video.
                </video>
              </div>
              <p className="text-gray-600">
                Processo di incisione e texturizzazione della piastra in ottone con macchinari di precisione
              </p>
            </div>

            {/* 2. Foto della piastra finita */}
            <div className="text-center">
              <div className="bg-amber-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl font-bold text-amber-600">2</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Piastra Completata</h3>
              <div className="bg-gray-50 rounded-xl p-4 mb-4">
                <img
                  src="https://customer-assets.emergentagent.com/job_modern-ideal/artifacts/jbu9ppf0_foto%20piastra%202.jpeg"
                  alt="Piastra per stampa su pelle completata"
                  className="w-full h-64 object-cover rounded-lg shadow-lg"
                />
              </div>
              <p className="text-gray-600">
                Piastra finita con texture personalizzata, pronta per la stampa su pelle
              </p>
            </div>

            {/* 3. Foto del prodotto finito */}
            <div className="text-center">
              <div className="bg-amber-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl font-bold text-amber-600">3</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Risultato Finale</h3>
              <div className="bg-gray-50 rounded-xl p-4 mb-4">
                <img
                  src="https://customer-assets.emergentagent.com/job_modern-ideal/artifacts/kzmcquh5_sottopiede%203.jpeg"
                  alt="Pelle stampata con texture"
                  className="w-full h-64 object-cover rounded-lg shadow-lg"
                />
              </div>
              <p className="text-gray-600">
                Sottopiede in cuoio stampato con la texture della piastra, risultato finale perfetto
              </p>
            </div>
          </div>

          {/* Call to action */}
          <div className="text-center mt-12">
            <div className="bg-amber-50 rounded-2xl p-8 inline-block">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Realizziamo piastre personalizzate per ogni esigenza
              </h3>
              <p className="text-gray-600 mb-4">
                Texture personalizzate, loghi aziendali e pattern unici per la stampa su pelle
              </p>
              <div className="text-amber-600 font-medium">
                Contattaci per un preventivo personalizzato
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Stamps;